import {
	createTheme,
	type MantineColorsTuple,
	type MantineThemeOverride,
} from "@mantine/core";

/**
 * Golden Record palette for Mantine.
 *
 * Mantine picks filled buttons from `primaryShade` and hover from the next
 * index, and in dark mode it paints color text from shade 4, outlines from
 * shade (primaryShade - 4), and light-variant text from shade (primaryShade - 5).
 * With primaryShade 6, those land on Record Gold (#C9A227) and the hover shade
 * lands on #E5C77A. The tuple is arranged for that math, not as a lightness ramp.
 */
function accentScale(
	filled: string,
	hover: string,
	deep: string,
): MantineColorsTuple {
	return [
		"#F4EFE4",
		hover,
		filled,
		hover,
		filled,
		deep,
		filled,
		hover,
		filled,
		deep,
	];
}

const gold = accentScale("#C9A227", "#E5C77A", "#8C6A3D");
const danger = accentScale("#B5503C", "#C46A56", "#8A3C2C");
const warning = accentScale("#C9922E", "#E0B15A", "#8C6A3D");
const success = accentScale("#5E7D62", "#7A9A7C", "#3E5644");

/** Surfaces from the marketing tokens: 7 canvas, 6 card, 8 panel. */
const dark: MantineColorsTuple = [
	"#F2F2EF",
	"#D6D2C6",
	"#A8A08C",
	"#8F8878",
	"#393731",
	"#242422",
	"#1B1B1A",
	"#141413",
	"#101011",
	"#0C0C0D",
];

export const theme: MantineThemeOverride = createTheme({
	primaryColor: "gold",
	primaryShade: 6,
	defaultRadius: 0,
	autoContrast: true,
	luminanceThreshold: 0.3,
	white: "#F2F2EF",
	black: "#141413",
	fontFamily: 'Inter, system-ui, "Segoe UI", Arial, sans-serif',
	fontFamilyMonospace:
		'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace',
	lineHeights: {
		xs: "1.45",
		sm: "1.5",
		md: "1.62",
		lg: "1.62",
		xl: "1.62",
	},
	headings: {
		fontFamily: 'Archivo, "Helvetica Neue", Arial, sans-serif',
		fontWeight: "700",
		sizes: {
			h1: { fontSize: "2.5rem", lineHeight: "1.06", fontWeight: "700" },
			h2: { fontSize: "1.75rem", lineHeight: "1.06", fontWeight: "700" },
			h3: { fontSize: "1.25rem", lineHeight: "1.25", fontWeight: "600" },
			h4: { fontSize: "1.125rem", lineHeight: "1.25", fontWeight: "600" },
			h5: { fontSize: "1rem", lineHeight: "1.25", fontWeight: "600" },
			h6: { fontSize: "0.875rem", lineHeight: "1.25", fontWeight: "600" },
		},
	},
	colors: {
		gold,
		dark,
		red: danger,
		pink: danger,
		yellow: warning,
		orange: warning,
		green: success,
		teal: success,
		// Old accent names resolve to Record Gold so existing color props stay gold.
		blue: gold,
		cyan: gold,
		violet: gold,
		grape: gold,
		indigo: gold,
	},
	radius: {
		xs: "0px",
		sm: "0px",
		md: "0px",
		lg: "0px",
		xl: "0px",
	},
	shadows: {
		xs: "none",
		sm: "none",
		md: "none",
		lg: "none",
		xl: "none",
	},
	defaultGradient: { from: "gold", to: "gold", deg: 0 },
});
